import { apiRequest, queryString } from "./http";
import type {
  ApiListParams,
  TaskDetailMutation,
  TaskDetailRecord,
  TaskKind,
  TaskMutation,
  TaskRecord,
  TaskAssignment,
  TemplateTaskMutation,
  TemplateTaskRecord,
  TemplateType,
} from "./generated/openapi";

export type { TaskKind } from "./generated/openapi";

export const TASK_KIND_OPTIONS = [
  ["general", "General"],
  ["meeting_details", "Meeting details"],
  ["documents", "Documents"],
  ["properties", "Example properties"],
  ["transferee_notes", "Transferee notes"],
  ["ecr", "Entry condition report"],
  ["lease", "Lease"],
  ["utilities", "Utilities"],
  ["airport_pickup", "Airport pickup"],
  ["cleaner", "Cleaner"],
  ["declutter", "Declutter"],
  ["quote", "Quote"],
  ["insurance", "Insurance"],
  ["rubbish", "Rubbish collection"],
  ["home_visit", "Home visit"],
  ["hotel_transfer", "Hotel transfer"],
  ["box_delivery", "Box delivery"],
  ["elevator_access", "Elevator access"],
] as const satisfies readonly (readonly [TaskKind, string])[];

export const taskApi = {
  list: (params: ApiListParams = {}) =>
    apiRequest<TaskRecord[]>(`/api/tasks${queryString(params)}`),

  listForProject: (projectId: string | number, params: ApiListParams = {}) =>
    apiRequest<TaskRecord[]>(
      `/api/projects/${projectId}/tasks${queryString(params)}`,
    ),

  get: (id: string | number) => apiRequest<TaskRecord>(`/api/tasks/${id}`),

  create: (payload: TaskMutation) =>
    apiRequest<TaskRecord>("/api/tasks", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  setupProjectTasks: (projectId: string | number, payload: TaskMutation = {}) =>
    apiRequest<TaskRecord[]>(`/api/projects/${projectId}/tasks/setup`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  update: (id: string | number, payload: TaskMutation) =>
    apiRequest<TaskRecord>(`/api/tasks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  updateStatus: (id: string | number, status: string) =>
    apiRequest<TaskRecord>(`/api/tasks/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  upsert: (payload: TaskMutation | TaskMutation[]) =>
    apiRequest<TaskRecord | TaskRecord[]>("/api/tasks/upsert", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  listAssigneesForProject: (projectId: string | number) =>
    apiRequest<TaskAssignment[]>(`/api/projects/${projectId}/task-assignees`),

  assign: (taskId: string, userId: string) =>
    apiRequest<TaskAssignment>(`/api/tasks/${taskId}/assignees`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    }),

  unassign: (taskId: string, userId: string) =>
    apiRequest<TaskAssignment>(`/api/tasks/${taskId}/assignees/${userId}`, {
      method: "DELETE",
    }),
};

export const templateTaskApi = {
  listTypes: () => apiRequest<TemplateType[]>("/api/template-tasks/types"),

  createType: (name: string) =>
    apiRequest<TemplateType>("/api/template-tasks/types", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),

  renameType: (id: number, name: string) =>
    apiRequest<TemplateType>(`/api/template-tasks/types/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  list: (params: ApiListParams = {}) =>
    apiRequest<TemplateTaskRecord[]>(
      `/api/template-tasks${queryString(params)}`,
    ),

  create: (payload: TemplateTaskMutation) =>
    apiRequest<TemplateTaskRecord>("/api/template-tasks", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  update: (id: string | number, payload: TemplateTaskMutation) =>
    apiRequest<TemplateTaskRecord>(`/api/template-tasks/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  delete: (id: string | number) =>
    apiRequest<TemplateTaskRecord>(`/api/template-tasks/${id}`, {
      method: "DELETE",
    }),

  move: (taskId: string | number, direction: string) =>
    apiRequest<TemplateTaskRecord>("/api/template-tasks/move", {
      method: "POST",
      body: JSON.stringify({ task_id: taskId, direction }),
    }),
};

export const taskDetailApi = {
  list: (kind: string, params: ApiListParams = {}) =>
    apiRequest<TaskDetailRecord[]>(
      `/api/task-details/${kind}${queryString(params)}`,
    ),

  create: (kind: string, payload: TaskDetailMutation) =>
    apiRequest<TaskDetailRecord>(`/api/task-details/${kind}`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  upsert: (kind: string, payload: TaskDetailMutation) =>
    apiRequest<TaskDetailRecord[]>(`/api/task-details/${kind}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  update: (kind: string, id: string | number, payload: TaskDetailMutation) =>
    apiRequest<TaskDetailRecord>(`/api/task-details/${kind}/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  delete: (kind: string, id: string | number) =>
    apiRequest<TaskDetailRecord>(`/api/task-details/${kind}/${id}`, {
      method: "DELETE",
    }),
};
